import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../../api/api';
import BudgetSummaryCards from './components/BudgetSummaryCards';
import ErrorBoundary from '../../components/ErrorBoundary';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { Button } from '../../components/ui/Button';

const BasicBudget = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDataEntry, setShowDataEntry] = useState(false);
  const [existingData, setExistingData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Şehir arama ve ekleme için state'ler
  const [citySearchTerm, setCitySearchTerm] = useState({});
  const [showCityDropdown, setShowCityDropdown] = useState({});
  const [isAddingNewCity, setIsAddingNewCity] = useState(false);
  const [newCityName, setNewCityName] = useState('');

  // Pazar arama ve ekleme için state'ler
  const [marketSearchTerm, setMarketSearchTerm] = useState({});
  const [showMarketDropdown, setShowMarketDropdown] = useState({});
  const [isAddingNewMarket, setIsAddingNewMarket] = useState(false);
  const [newMarketName, setNewMarketName] = useState('');

  // Ülke arama ve ekleme için state'ler
  const [countrySearchTerm, setCountrySearchTerm] = useState({});
  const [showCountryDropdown, setShowCountryDropdown] = useState({});
  const [isAddingNewCountry, setIsAddingNewCountry] = useState(false);
  const [newCountryName, setNewCountryName] = useState('');

  // Acenta arama ve ekleme için state'ler
  const [agencySearchTerm, setAgencySearchTerm] = useState({});
  const [showAgencyDropdown, setShowAgencyDropdown] = useState({});
  const [isAddingNewAgency, setIsAddingNewAgency] = useState(false);
  const [newAgencyName, setNewAgencyName] = useState('');

  // Otel arama ve ekleme için state'ler
  const [hotelSearchTerm, setHotelSearchTerm] = useState({});
  const [showHotelDropdown, setShowHotelDropdown] = useState({});
  const [isAddingNewHotel, setIsAddingNewHotel] = useState(false);
  const [newHotelName, setNewHotelName] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    maingroup: '',
    budgettype: '',
    year: new Date().getFullYear(),
    month: null,

    // Step 2: Dynamic fields based on maingroup
    entries: []
  });

  // Single row data for Step 3 - Multiple rows
  const [singleRowData, setSingleRowData] = useState([]);

  // Sales Call ülke filtresi
  const [countryFilter, setCountryFilter] = useState([]);
  const [isCountryFilterDropdownOpen, setIsCountryFilterDropdownOpen] = useState(false);
  const countryFilterDropdownRef = useRef(null);

  // Pazar filtresi
  const [marketFilter, setMarketFilter] = useState([]);
  const [isMarketFilterDropdownOpen, setIsMarketFilterDropdownOpen] = useState(false);
  const [marketFilterSearch, setMarketFilterSearch] = useState('');
  const marketFilterDropdownRef = useRef(null);

  // Şehir filtresi
  const [cityFilter, setCityFilter] = useState([]);
  const [isCityFilterDropdownOpen, setIsCityFilterDropdownOpen] = useState(false);
  const [cityFilterSearch, setCityFilterSearch] = useState('');
  const cityFilterDropdownRef = useRef(null);

  // Ülke filtresi arama
  const [countryFilterSearch, setCountryFilterSearch] = useState('');

  // Sıralama state'i
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc' // 'asc' veya 'desc'
  });

  // Add new row
  const addNewRow = () => {
    const selectedGroupForNewRow = (referenceData.maingroups || []).find(
      (g) => parseInt(g.id) === parseInt(formData.maingroup)
    );
    const isSalesCallGroup = selectedGroupForNewRow?.maingroup_name === 'Sales Call';
    const prefilledCountry =
      isSalesCallGroup && countryFilter.length === 1 ? countryFilter[0] : '';
    const newRow = {
      id: `row-${Date.now()}`,
      market: '',
      country: prefilledCountry,
      city: '',
      agency: '',
      hotel: '',
      start_date: '',
      end_date: '',
      pax: '',
      pp: '',
      day_count: '',
      summary: '',
      region: '',
      eur_curr: '',
      budget_month: formData.month,
      subgroupAmounts: {},
      subgroupPax: {} // AKKA Bonus için PAX verileri
    };
    setSingleRowData(prev => [...prev, newRow]);
  };

  // Update row data
  const updateRowData = (rowId, field, value) => {
    setSingleRowData(prev => prev.map(row =>
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  // Update subgroup amount for specific row
  const updateSubgroupAmount = (rowId, subgroupId, amount) => {
    setSingleRowData(prev => prev.map(row =>
      row.id === rowId
        ? {
          ...row,
          subgroupAmounts: {
            ...row.subgroupAmounts,
            [subgroupId]: amount
          }
        }
        : row
    ));
  };

  // Update subgroup PAX for specific row (AKKA Bonus için)
  const updateSubgroupPax = (rowId, subgroupId, pax) => {
    setSingleRowData(prev => prev.map(row =>
      row.id === rowId
        ? {
          ...row,
          subgroupPax: {
            ...row.subgroupPax || {},
            [subgroupId]: pax
          }
        }
        : row
    ));
  };

  // Remove row
  const removeRow = (rowId) => {
    setSingleRowData(prev => prev.filter(row => row.id !== rowId));
  };

  // Duplicate row
  const duplicateRow = (rowId) => {
    const rowToDuplicate = singleRowData.find(row => row.id === rowId);
    if (!rowToDuplicate) return;

    const duplicatedRow = {
      ...rowToDuplicate,
      id: `row-${Date.now()}`,
      subgroupAmounts: {}, // Tutarları temizle
      subgroupPax: {} // PAX verilerini temizle
    };

    setSingleRowData(prev => [...prev, duplicatedRow]);
  };

  // Reference data
  const [referenceData, setReferenceData] = useState({
    maingroups: [],
    subgroups: [],
    markets: [],
    types: [],
    countries: [],
    cities: [],
    agencies: [],
    hotels: []
  });

  useEffect(() => {
    const selectedGroup = (referenceData.maingroups || []).find(
      (g) => parseInt(g.id) === parseInt(formData.maingroup)
    );
    const isAkkaBonus = selectedGroup?.maingroup_name?.includes('AKKA Bonus') || false;
    // AKKA Bonus dışındaki gruplar için filtreleri temizle
    if (isAkkaBonus) {
      if (marketFilter.length > 0) {
        setMarketFilter([]);
        setIsMarketFilterDropdownOpen(false);
      }
      if (countryFilter.length > 0) {
        setCountryFilter([]);
        setIsCountryFilterDropdownOpen(false);
      }
      if (cityFilter.length > 0) {
        setCityFilter([]);
        setIsCityFilterDropdownOpen(false);
      }
    }
  }, [formData.maingroup, referenceData.maingroups]);

  useEffect(() => {
    if (countryFilter.length === 0) return;
    const normalize = (value) => (value || '').trim().toLowerCase();
    const existingCountries = new Set(
      singleRowData
        .map((row) => normalize(row.country))
        .filter((value) => value.length > 0)
    );
    const filteredSelections = countryFilter.filter(
      (country) => existingCountries.has(normalize(country))
    );
    if (filteredSelections.length !== countryFilter.length) {
      setCountryFilter(filteredSelections);
    }
  }, [singleRowData, countryFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryFilterDropdownRef.current &&
        !countryFilterDropdownRef.current.contains(event.target)
      ) {
        setIsCountryFilterDropdownOpen(false);
      }
      if (
        marketFilterDropdownRef.current &&
        !marketFilterDropdownRef.current.contains(event.target)
      ) {
        setIsMarketFilterDropdownOpen(false);
      }
      if (
        cityFilterDropdownRef.current &&
        !cityFilterDropdownRef.current.contains(event.target)
      ) {
        setIsCityFilterDropdownOpen(false);
      }
    };
    if (isCountryFilterDropdownOpen || isMarketFilterDropdownOpen || isCityFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCountryFilterDropdownOpen, isMarketFilterDropdownOpen, isCityFilterDropdownOpen]);

  // Dropdown kapandığında arama alanlarını temizle
  useEffect(() => {
    if (!isMarketFilterDropdownOpen) {
      setMarketFilterSearch('');
    }
  }, [isMarketFilterDropdownOpen]);

  useEffect(() => {
    if (!isCountryFilterDropdownOpen) {
      setCountryFilterSearch('');
    }
  }, [isCountryFilterDropdownOpen]);

  useEffect(() => {
    if (!isCityFilterDropdownOpen) {
      setCityFilterSearch('');
    }
  }, [isCityFilterDropdownOpen]);

  // Ana grup bazında alan konfigürasyonu
  const getFieldConfig = (maingroupName) => {
    switch (maingroupName) {
      case 'Web Yazılım':
        return {
          showFields: ['hotel', 'market', 'country', 'city', 'subgroup', 'summary', 'budget_amount', 'budget_month'],
          requiredFields: ['budget_amount'],
          fieldLabels: {
            hotel: 'Otel',
            market: 'Pazar',
            country: 'Ülke',
            city: 'Şehir',
            subgroup: 'Alt Grup',
            summary: 'Özet',
            budget_amount: 'Tutar',
            budget_month: 'Ay'
          }
        };
      case 'Sales Call':
        return {
          showFields: ['subgroup', 'market', 'country', 'city', 'day_count', 'pax', 'pp', 'budget_amount', 'budget_month'],
          requiredFields: ['subgroup', 'market', 'budget_amount'],
          fieldLabels: {
            subgroup: 'Alt Grup',
            market: 'Pazar',
            country: 'Ülke',
            city: 'Şehir',
            day_count: 'Gün Sayısı',
            pax: 'Kişi Sayısı',
            pp: 'PP',
            budget_amount: 'Tutar',
            budget_month: 'Ay'
          }
        };
      case 'Fuar Detay':
        return {
          showFields: ['subgroup', 'market', 'country', 'city', 'agency', 'start_date', 'end_date', 'pax', 'budget_amount'],
          requiredFields: ['subgroup', 'market', 'budget_amount'],
          fieldLabels: {
            subgroup: 'Alt Grup',
            market: 'Pazar',
            country: 'Ülke',
            city: 'Şehir',
            agency: 'Acenta',
            start_date: 'Başlangıç Tarihi',
            end_date: 'Bitiş Tarihi',
            pax: 'PAX',
            budget_amount: 'Tutar'
          }
        };
      case 'İlan Reklam':
        return {
          showFields: ['country', 'city', 'agency', 'subgroup', 'market', 'budget_amount', 'budget_month', 'summary'],
          requiredFields: ['subgroup', 'market', 'budget_amount'],
          fieldLabels: {
            country: 'Ülke',
            city: 'Şehir',
            agency: 'Acenta',
            subgroup: 'Alt Grup',
            market: 'Pazar',
            budget_amount: 'Tutar',
            budget_month: 'Ay',
            summary: 'Özet'
          }
        };
      case 'Temsil Ağırlama':
        return {
          showFields: ['subgroup', 'market', 'country', 'city', 'hotel', 'pax', 'budget_amount', 'budget_month'],
          requiredFields: ['subgroup', 'hotel', 'pax', 'budget_amount'],
          fieldLabels: {
            subgroup: 'Alt Grup',
            market: 'Pazar',
            country: 'Ülke',
            city: 'Şehir',
            hotel: 'Otel',
            pax: 'PAX',
            budget_amount: 'Tutar',
            budget_month: 'Ay'
          }
        };
      case 'AKKA Bonus':
        return {
          showFields: ['subgroup', 'budget_amount'],
          requiredFields: ['subgroup', 'budget_amount'],
          fieldLabels: {
            subgroup: 'Alt Grup',
            budget_amount: 'Tutar'
          }
        };
      case 'Dijital Pazarlama':
        return {
          showFields: ['subgroup', 'market', 'country', 'city', 'agency', 'region', 'eur_curr', 'budget_amount', 'budget_month', 'summary'],
          requiredFields: ['subgroup', 'market', 'budget_amount'],
          fieldLabels: {
            subgroup: 'Alt Grup',
            market: 'Pazar',
            country: 'Ülke',
            city: 'Şehir',
            agency: 'Acenta',
            region: 'Bölge',
            eur_curr: 'Euro Kur',
            budget_amount: 'Tutar',
            budget_month: 'Ay',
            summary: 'Özet'
          }
        };
      default:
        return {
          showFields: ['subgroup', 'market', 'country', 'city', 'agency', 'budget_amount'],
          requiredFields: ['subgroup', 'market', 'budget_amount'],
          fieldLabels: {
            subgroup: 'Alt Grup',
            market: 'Pazar',
            country: 'Ülke',
            city: 'Şehir',
            agency: 'Acenta',
            budget_amount: 'Tutar'
          }
        };
    }
  };

  // Şehir filtreleme fonksiyonu
  const getFilteredCities = (rowId) => {
    const searchTerm = citySearchTerm[rowId] || '';
    if (!searchTerm) return referenceData.cities;

    return referenceData.cities.filter(city =>
      city.city_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Pazar filtreleme fonksiyonu
  const getFilteredMarkets = (rowId) => {
    const searchTerm = marketSearchTerm[rowId] || '';
    if (!searchTerm) return referenceData.markets;

    return referenceData.markets.filter(market =>
      market.budget_market_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Ülke filtreleme fonksiyonu
  const getFilteredCountries = (rowId) => {
    const searchTerm = countrySearchTerm[rowId] || '';
    if (!searchTerm) return referenceData.countries;

    return referenceData.countries.filter(country =>
      country.country_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Acenta filtreleme fonksiyonu
  const getFilteredAgencies = (rowId) => {
    const searchTerm = agencySearchTerm[rowId] || '';
    if (!searchTerm) return referenceData.agencies;

    return referenceData.agencies.filter(agency =>
      agency.agency_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Otel filtreleme fonksiyonu
  const getFilteredHotels = (rowId) => {
    const searchTerm = hotelSearchTerm[rowId] || '';
    if (!searchTerm) return referenceData.hotels;

    return referenceData.hotels.filter(hotel =>
      hotel.hotel_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Yeni şehir ekleme fonksiyonu
  const handleAddNewCity = async () => {
    if (!newCityName.trim()) return;

    try {
      const response = await apiRequest('/marketing-budget/cities/', {
        method: 'POST',
        body: JSON.stringify({
          city_name: newCityName.trim()
        })
      });

      // Reference data'yı yenile
      const citiesData = await apiRequest('/marketing-budget/cities/');
      setReferenceData(prev => ({ ...prev, cities: citiesData.results || citiesData }));

      setNewCityName('');
      setIsAddingNewCity(false);

      alert('Yeni şehir eklendi!');
    } catch (error) {
      // Silently handle error
      alert('Şehir eklenirken bir hata oluştu.');
    }
  };

  // Yeni pazar ekleme fonksiyonu
  const handleAddNewMarket = async () => {
    if (!newMarketName.trim()) return;

    try {
      const response = await apiRequest('/marketing-budget/markets/', {
        method: 'POST',
        body: JSON.stringify({
          budget_market_name: newMarketName.trim()
        })
      });

      // Reference data'yı yenile
      const marketsData = await apiRequest('/marketing-budget/markets/');
      setReferenceData(prev => ({ ...prev, markets: marketsData.results || marketsData }));

      setNewMarketName('');
      setIsAddingNewMarket(false);

      alert('Yeni pazar eklendi!');
    } catch (error) {
      // Silently handle error
      alert('Pazar eklenirken bir hata oluştu.');
    }
  };

  // Yeni ülke ekleme fonksiyonu
  const handleAddNewCountry = async () => {
    if (!newCountryName.trim()) return;

    try {
      const response = await apiRequest('/marketing-budget/countries/', {
        method: 'POST',
        body: JSON.stringify({
          country_name: newCountryName.trim()
        })
      });

      // Reference data'yı yenile
      const countriesData = await apiRequest('/marketing-budget/countries/');
      setReferenceData(prev => ({ ...prev, countries: countriesData.results || countriesData }));

      setNewCountryName('');
      setIsAddingNewCountry(false);

      alert('Yeni ülke eklendi!');
    } catch (error) {
      // Silently handle error
      alert('Ülke eklenirken bir hata oluştu.');
    }
  };

  // Yeni acenta ekleme fonksiyonu
  const handleAddNewAgency = async () => {
    if (!newAgencyName.trim()) return;

    try {
      const response = await apiRequest('/marketing-budget/agencies/', {
        method: 'POST',
        body: JSON.stringify({
          agency_name: newAgencyName.trim()
        })
      });

      // Reference data'yı yenile
      const agenciesData = await apiRequest('/marketing-budget/agencies/');
      setReferenceData(prev => ({ ...prev, agencies: agenciesData.results || agenciesData }));

      setNewAgencyName('');
      setIsAddingNewAgency(false);

      alert('Yeni acenta eklendi!');
    } catch (error) {
      // Silently handle error
      alert('Acenta eklenirken bir hata oluştu.');
    }
  };

  // Yeni otel ekleme fonksiyonu
  const handleAddNewHotel = async () => {
    if (!newHotelName.trim()) return;

    try {
      const response = await apiRequest('/marketing-budget/hotels/', {
        method: 'POST',
        body: JSON.stringify({
          hotel_name: newHotelName.trim()
        })
      });

      // Reference data'yı yenile
      const hotelsData = await apiRequest('/marketing-budget/hotels/');
      setReferenceData(prev => ({ ...prev, hotels: hotelsData.results || hotelsData }));

      setNewHotelName('');
      setIsAddingNewHotel(false);

      alert('Yeni otel eklendi!');
    } catch (error) {
      // Silently handle error
      alert('Otel eklenirken bir hata oluştu.');
    }
  };

  // İlk satırı otomatik ekle
  useEffect(() => {
    if (singleRowData.length === 0 && formData.maingroup) {
      addNewRow();
    }
  }, [formData.maingroup]);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Tüm açık dropdown'ları kontrol et
      Object.keys(showCityDropdown).forEach(rowId => {
        if (showCityDropdown[rowId]) {
          const dropdownElement = document.getElementById(`city-dropdown-${rowId}`);
          const inputElement = document.getElementById(`city-input-${rowId}`);

          if (dropdownElement && inputElement &&
            !dropdownElement.contains(event.target) &&
            !inputElement.contains(event.target)) {
            setShowCityDropdown(prev => ({ ...prev, [rowId]: false }));
          }
        }
      });

      Object.keys(showMarketDropdown).forEach(rowId => {
        if (showMarketDropdown[rowId]) {
          const dropdownElement = document.getElementById(`market-dropdown-${rowId}`);
          const inputElement = document.getElementById(`market-input-${rowId}`);

          if (dropdownElement && inputElement &&
            !dropdownElement.contains(event.target) &&
            !inputElement.contains(event.target)) {
            setShowMarketDropdown(prev => ({ ...prev, [rowId]: false }));
          }
        }
      });

      Object.keys(showCountryDropdown).forEach(rowId => {
        if (showCountryDropdown[rowId]) {
          const dropdownElement = document.getElementById(`country-dropdown-${rowId}`);
          const inputElement = document.getElementById(`country-input-${rowId}`);

          if (dropdownElement && inputElement &&
            !dropdownElement.contains(event.target) &&
            !inputElement.contains(event.target)) {
            setShowCountryDropdown(prev => ({ ...prev, [rowId]: false }));
          }
        }
      });

      Object.keys(showAgencyDropdown).forEach(rowId => {
        if (showAgencyDropdown[rowId]) {
          const dropdownElement = document.getElementById(`agency-dropdown-${rowId}`);
          const inputElement = document.getElementById(`agency-input-${rowId}`);

          if (dropdownElement && inputElement &&
            !dropdownElement.contains(event.target) &&
            !inputElement.contains(event.target)) {
            setShowAgencyDropdown(prev => ({ ...prev, [rowId]: false }));
          }
        }
      });

      Object.keys(showHotelDropdown).forEach(rowId => {
        if (showHotelDropdown[rowId]) {
          // Normal ve AKKA Bonus için farklı ID formatları
          const dropdownElement = document.getElementById(`hotel-dropdown-${rowId}`) ||
            document.getElementById(`hotel-dropdown-akka-${rowId}`);
          const inputElement = document.getElementById(`hotel-input-${rowId}`) ||
            document.getElementById(`hotel-input-akka-${rowId}`);

          if (dropdownElement && inputElement &&
            !dropdownElement.contains(event.target) &&
            !inputElement.contains(event.target)) {
            setShowHotelDropdown(prev => ({ ...prev, [rowId]: false }));
          }
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCityDropdown, showMarketDropdown, showCountryDropdown, showAgencyDropdown, showHotelDropdown]);

  // Veri yükleme mantığını tek bir akışta birleştirelim
  const fetchAllData = async () => {
    try {
      setLoading(true);
      // Her iki yüklemeyi de bekle
      await Promise.all([
        fetchReferenceData(),
        fetchExistingData()
      ]);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [refreshTrigger]);

  useEffect(() => {
    // Global yükleme sinyali gönder
    window.dispatchEvent(new CustomEvent('app_loading', { detail: loading }));
  }, [loading]);

  const fetchReferenceData = async () => {
    try {
      // setLoading burada çağrılmıyor, üst fonksiyonda (fetchAllData) yönetiliyor
      const [maingroups, subgroups, markets, types, countries, cities, agencies, hotels] = await Promise.all([
        apiRequest('/marketing-budget/maingroups/'),
        apiRequest('/marketing-budget/subgroups/'),
        apiRequest('/marketing-budget/markets/'),
        apiRequest('/marketing-budget/types/'),
        apiRequest('/marketing-budget/countries/'),
        apiRequest('/marketing-budget/cities/'),
        apiRequest('/marketing-budget/agencies/'),
        apiRequest('/marketing-budget/hotels/')
      ]);

      setReferenceData({
        maingroups: maingroups.results || maingroups,
        subgroups: subgroups.results || subgroups,
        markets: markets.results || markets,
        types: types.results || types,
        countries: countries.results || countries,
        cities: cities.results || cities,
        agencies: agencies.results || agencies,
        hotels: hotels.results || hotels
      });
    } catch (error) {
      setError('Referans verileri yüklenirken hata oluştu');
      throw error; // Üst fonksiyona hatayı ilet
    }
  };

  const fetchExistingData = async () => {
    try {
      const response = await apiRequest('/marketing-budget/registries/');
      const data = response.results || response;

      // Verileri ana grup ve bütçe tipine göre grupla
      const groupedData = data.reduce((groups, item) => {
        const key = `${item.maingroup_name}-${item.budgettype_name}-${item.budget_year}`;
        if (!groups[key]) {
          groups[key] = {
            maingroup_name: item.maingroup_name,
            budgettype_name: item.budgettype_name,
            budget_year: item.budget_year,
            budget_month: item.budget_month,
            total_amount: 0,
            entries: []
          };
        }
        groups[key].entries.push(item);
        groups[key].total_amount += parseFloat(item.budget_amount || 0);
        return groups;
      }, {});

      setExistingData(Object.values(groupedData));
    } catch (error) {
      console.error('Bütçe verileri yüklenirken hata:', error);
      throw error;
    }
  };


  // Entry management
  const addEntry = () => {
    const newEntry = {
      id: `temp-${Date.now()}`,
      subgroup: '',
      market: '',
      country: '',
      city: '',
      agency: '',
      budget_amount: '',
      // Diğer alanlar boş olarak başlar
      hotel: '',
      start_date: '',
      end_date: '',
      pax: '',
      pp: '',
      day_count: '',
      summary: '',
      budget_month: ''
    };

    setFormData(prev => ({
      ...prev,
      entries: [...prev.entries, newEntry]
    }));
  };

  const updateEntry = (entryId, field, value) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map(entry =>
        entry.id === entryId ? { ...entry, [field]: value } : entry
      )
    }));
  };

  const removeEntry = (entryId) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.filter(entry => entry.id !== entryId)
    }));
  };

  const duplicateEntry = (entryId) => {
    const entryToDuplicate = formData.entries.find(entry => entry.id === entryId);
    if (!entryToDuplicate) return;

    const duplicatedEntry = {
      ...entryToDuplicate,
      id: `temp-${Date.now()}`,
      budget_amount: '' // Tutarı temizle
    };

    setFormData(prev => ({
      ...prev,
      entries: [...prev.entries, duplicatedEntry]
    }));
  };

  // Save function
  const handleSave = async () => {
    // Eğer singleRowData boşsa, formData.entries kullan
    const dataToUse = singleRowData.length > 0 ? singleRowData : formData.entries;

    if (dataToUse.length === 0) {
      alert('Kaydedilecek veri bulunamadı!');
      return;
    }

    try {
      setLoading(true);

      const selectedGroup = referenceData.maingroups.find(g => g.id === parseInt(formData.maingroup));
      const selectedType = referenceData.types.find(t => t.id === parseInt(formData.budgettype));

      // AKKA Bonus kontrolü - daha önce kontrol et
      const isAkkaBonus = selectedGroup?.maingroup_name?.includes('AKKA Bonus') || false;

      // singleRowData'dan entries oluştur
      const entriesToSave = [];
      dataToUse.forEach(row => {

        // AKKA Bonus için özel mantık: Her satır için her ay (subgroup) için ayrı kayıt
        if (isAkkaBonus) {
          // Tüm subgroup'ları topla (hem subgroupAmounts hem de subgroupPax'ten)
          const allSubgroupIds = new Set([
            ...Object.keys(row.subgroupAmounts || {}),
            ...Object.keys(row.subgroupPax || {})
          ]);

          allSubgroupIds.forEach(subgroupId => {
            const amount = row.subgroupAmounts?.[subgroupId] || '';
            const pax = row.subgroupPax?.[subgroupId] || '';
            const numericAmount = parseFloat(amount);
            const numericPax = parseFloat(pax);

            // En az bir değer (TUTAR veya PAX) varsa kayıt oluştur
            if ((amount !== '' && amount !== null && amount !== undefined && !isNaN(numericAmount) && numericAmount >= 0) ||
              (pax !== '' && pax !== null && pax !== undefined && !isNaN(numericPax) && numericPax >= 0)) {

              const subgroup = (referenceData.subgroups || []).find(sg => parseInt(sg.id) === parseInt(subgroupId));

              if (!subgroup) {
                return;
              }

              // Eğer düzenleme modundaysa, orijinal entry'yi bul
              let originalEntry = null;
              if (row.originalEntries && row.originalEntries.length > 0) {
                originalEntry = row.originalEntries.find(oe => {
                  const oeSubgroupId = oe.subgroup_id || oe.subgroup || oe.subgroup_name;
                  return oeSubgroupId == subgroupId;
                });
              }

              entriesToSave.push({
                id: originalEntry?.id || null, // Varsa güncelleme, yoksa yeni kayıt
                subgroup: subgroupId,
                subgroup_name: subgroup?.subgroup_name || `Subgroup ${subgroupId}`,
                market: null, // AKKA Bonus için market null
                country: '',
                city: '',
                agency: '',
                hotel: row.hotel || '', // Otel adı
                day_count: 0,
                pax: numericPax || 0, // PAX değeri
                pp: 0,
                summary: '',
                region: '',
                eur_curr: 0,
                budget_amount: numericAmount || 0, // TUTAR değeri
                budget_month: null // AKKA Bonus için ay null
              });
            }
          });
        } else {
          // Normal mantık (AKKA Bonus dışı)
          // Eğer düzenleme modundaysa (originalEntries varsa)
          if (row.originalEntries && row.originalEntries.length > 0) {

            // Her orijinal entry'yi güncelle
            row.originalEntries.forEach(originalEntry => {
              const subgroupId = originalEntry.subgroup_id || originalEntry.subgroup || originalEntry.subgroup_name;
              const newAmount = row.subgroupAmounts[subgroupId];

              if (newAmount !== undefined) {
                entriesToSave.push({
                  id: originalEntry.id, // Orijinal ID'yi kullan
                  subgroup: subgroupId,
                  subgroup_name: originalEntry.subgroup_name,
                  market: row.market,
                  country: row.country,
                  city: row.city,
                  agency: row.agency,
                  hotel: row.hotel,
                  day_count: row.day_count || 0,
                  pax: row.pax || 0,
                  pp: row.pp || 0,
                  summary: row.summary, // Özet field'ını ekle
                  region: row.region || '',
                  eur_curr: row.eur_curr || 0,
                  budget_amount: parseFloat(newAmount), // Numeric olarak kaydet
                  budget_month: row.budget_month || formData.month
                });
              }
            });

            // Orijinal entries'de olmayan yeni alt grupları da ekle
            Object.entries(row.subgroupAmounts || {}).forEach(([subgroupId, amount]) => {
              const numericAmount = parseFloat(amount);
              if (amount !== '' && amount !== null && amount !== undefined && !isNaN(numericAmount) && numericAmount >= 0) {
                // Bu alt grup orijinal entries'de var mı kontrol et
                const existsInOriginal = row.originalEntries.some(originalEntry => {
                  const originalSubgroupId = originalEntry.subgroup_id || originalEntry.subgroup || originalEntry.subgroup_name;
                  return originalSubgroupId == subgroupId;
                });

                // Eğer orijinal entries'de yoksa yeni kayıt olarak ekle
                if (!existsInOriginal) {
                  const subgroup = (referenceData.subgroups || []).find(sg => parseInt(sg.id) === parseInt(subgroupId));

                  // Debug: Subgroup bulunamadıysa logla
                  if (!subgroup) {
                    // Yeni subgroup bulunamadı, atla
                    return;
                  }

                  entriesToSave.push({
                    id: null, // Yeni kayıt
                    subgroup: subgroupId,
                    subgroup_name: subgroup?.subgroup_name || `Subgroup ${subgroupId}`, // Fallback name
                    market: row.market,
                    country: row.country,
                    city: row.city,
                    agency: row.agency,
                    hotel: row.hotel,
                    day_count: row.day_count || 0,
                    pax: row.pax || 0,
                    pp: row.pp || 0,
                    summary: row.summary, // Özet field'ını ekle
                    region: row.region || '',
                    eur_curr: row.eur_curr || 0,
                    budget_amount: numericAmount, // Numeric olarak kaydet
                    budget_month: row.budget_month || formData.month
                  });
                }
              }
            });
          } else {
            // Yeni kayıt modu - subgroupAmounts'dan entries oluştur
            Object.entries(row.subgroupAmounts || {}).forEach(([subgroupId, amount]) => {
              const numericAmount = parseFloat(amount);
              if (amount !== '' && amount !== null && amount !== undefined && !isNaN(numericAmount) && numericAmount >= 0) {
                const subgroup = (referenceData.subgroups || []).find(sg => parseInt(sg.id) === parseInt(subgroupId));

                // Debug: Subgroup bulunamadıysa logla
                if (!subgroup) {
                  // Subgroup bulunamadı, atla
                  return;
                }

                entriesToSave.push({
                  id: null, // Yeni kayıt
                  subgroup: subgroupId,
                  subgroup_name: subgroup?.subgroup_name || `Subgroup ${subgroupId}`, // Fallback name
                  market: row.market,
                  country: row.country,
                  city: row.city,
                  agency: row.agency,
                  hotel: row.hotel,
                  day_count: row.day_count || 0,
                  pax: row.pax || 0,
                  pp: row.pp || 0,
                  summary: row.summary || '',
                  region: row.region || '',
                  eur_curr: row.eur_curr || 0,
                  budget_amount: numericAmount, // Numeric olarak kaydet
                  budget_month: row.budget_month || formData.month
                });
              }
            });
          }
        }
      });

      // Debug: Kaydedilecek entries'leri kontrol et
      // Bulk save için verileri hazırla
      const toCreate = [];
      const toUpdate = [];

      entriesToSave.forEach(entry => {
        const isUpdate = entry.id && !entry.id.toString().startsWith('temp-');

        const payload = {
          maingroup: formData.maingroup,
          subgroup: entry.subgroup || null,
          market: isAkkaBonus ? null : (entry.market || null),
          budgettype: formData.budgettype,
          budgettype_name: selectedType?.budget_type,
          budget_amount: entry.budget_amount,
          budget_year: formData.year,
          budget_month: isAkkaBonus ? null : (entry.budget_month || formData.month),
          budget_name: `${selectedGroup?.maingroup_name} - ${selectedType?.budget_type} (${formData.year})`,
          country: isAkkaBonus ? '' : (entry.country || ''),
          city: isAkkaBonus ? '' : (entry.city || ''),
          agency: isAkkaBonus ? '' : (entry.agency || ''),
          hotel_name: entry.hotel || '',
          start_date: entry.start_date || null,
          end_date: entry.end_date || null,
          pax: entry.pax || 0,
          pp: entry.pp || 0,
          day_count: entry.day_count || 0,
          summary: isAkkaBonus ? '' : (entry.summary || ''),
          region: isAkkaBonus ? '' : (entry.region || ''),
          eur_curr: isAkkaBonus ? 0 : (entry.eur_curr || 0)
        };

        if (isUpdate) {
          toUpdate.push({
            id: entry.id,
            ...payload
          });
        } else {
          toCreate.push(payload);
        }
      });

      let successMessage = '';
      if (toCreate.length > 0 || toUpdate.length > 0) {
        const response = await apiRequest('/marketing-budget/registries/bulk_save/', {
          method: 'POST',
          body: JSON.stringify({
            to_create: toCreate,
            to_update: toUpdate
          })
        });

        const created = response.created || 0;
        const updated = response.updated || 0;

        if (created > 0 && updated > 0) {
          successMessage = `${created} kayıt eklendi, ${updated} kayıt güncellendi!`;
        } else if (created > 0) {
          successMessage = `${created} kayıt başarıyla eklendi!`;
        } else if (updated > 0) {
          successMessage = `${updated} kayıt başarıyla güncellendi!`;
        } else {
          successMessage = 'İşlem başarıyla tamamlandı!';
        }
      }

      // Reset form
      setFormData({
        maingroup: '',
        budgettype: '',
        year: new Date().getFullYear(),
        month: null,
        entries: []
      });
      setSingleRowData([]); // Single row data'yı da temizle
      setShowDataEntry(false); // Ana sayfaya dön
      setRefreshTrigger(prev => prev + 1); // Veriyi yenile

      alert(successMessage);
    } catch (error) {
      // Silently handle error
      setError('Kayıt sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };


  // Step 3: Data Entry - Multi-Row Excel Style
  const renderStep3 = () => {
    // Güvenlik kontrolü - referenceData'nın yüklenmesini bekle
    if (!referenceData.maingroups || !referenceData.subgroups || !referenceData.markets) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Veriler yükleniyor...</p>
          </div>
        </div>
      );
    }

    const currentMaingroup = formData.maingroup;
    const selectedGroupId = parseInt(currentMaingroup);

    // Hem ID hem de isim ile dene (bazı durumlarda maingroup ismi gelebiliyor)
    const selectedGroup = (referenceData.maingroups || []).find(g =>
      parseInt(g.id) === selectedGroupId || g.maingroup_name === currentMaingroup
    );

    const fieldConfig = getFieldConfig(selectedGroup?.maingroup_name || '');
    const isSalesCall = selectedGroup?.maingroup_name === 'Sales Call';
    const isTemsilAgir = selectedGroup?.maingroup_name === 'Temsil Ağırlama';
    const isAkkaBonus = selectedGroup?.maingroup_name?.includes('AKKA Bonus') || false;
    const isDijitalPazarlama = selectedGroup?.maingroup_name === 'Dijital Pazarlama';
    const normalizeCountry = (value) => (value || '').trim().toLowerCase();
    const normalizeValue = (value) => (value || '').trim().toLowerCase();

    const currentSingleRowData = singleRowData || [];

    // Ülke seçenekleri
    const countryOptions = !isAkkaBonus
      ? Array.from(
        new Set(
          currentSingleRowData
            .map((row) => (row.country || '').trim())
            .filter((countryName) => countryName.length > 0)
        )
      ).sort((a, b) => a.localeCompare(b, 'tr-TR'))
      : [];

    // Pazar seçenekleri
    const marketOptions = !isAkkaBonus
      ? Array.from(
        new Set(
          currentSingleRowData
            .map((row) => {
              const rowMarketId = parseInt(row.market);
              const market = (referenceData.markets || []).find(m => parseInt(m.id) === rowMarketId);
              return market?.budget_market_name || '';
            })
            .filter((marketName) => marketName.length > 0)
        )
      ).sort((a, b) => a.localeCompare(b, 'tr-TR'))
      : [];

    // Şehir seçenekleri
    const cityOptions = !isAkkaBonus
      ? Array.from(
        new Set(
          currentSingleRowData
            .map((row) => (row.city || '').trim())
            .filter((cityName) => cityName.length > 0)
        )
      ).sort((a, b) => a.localeCompare(b, 'tr-TR'))
      : [];
    // Sıralama fonksiyonu
    const handleSort = (key) => {
      // Eğer aynı kolona tıklanıyorsa
      if (sortConfig.key === key) {
        // Artan sıralamadaysa -> Azalan
        if (sortConfig.direction === 'asc') {
          setSortConfig({ key, direction: 'desc' });
        }
        // Azalan sıralamadaysa -> Sıralama kaldır
        else if (sortConfig.direction === 'desc') {
          setSortConfig({ key: null, direction: 'asc' });
        }
      } else {
        // Farklı kolona tıklanıyorsa -> Artan sıralama
        setSortConfig({ key, direction: 'asc' });
      }
    };

    // Sıralanmış veri
    const getSortedData = (data) => {
      if (!sortConfig.key) return data;

      // Ay isimlerini sıralı hale getirmek için mapping
      const monthOrder = {
        'Ocak': 1, 'Şubat': 2, 'Mart': 3, 'Nisan': 4, 'Mayıs': 5, 'Haziran': 6,
        'Temmuz': 7, 'Ağustos': 8, 'Eylül': 9, 'Ekim': 10, 'Kasım': 11, 'Aralık': 12
      };

      const sorted = [...data].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Market için referenceData'dan isim al
        if (sortConfig.key === 'market') {
          const aMarketId = parseInt(a.market);
          const bMarketId = parseInt(b.market);
          aValue = (referenceData.markets || []).find(m => parseInt(m.id) === aMarketId)?.budget_market_name || '';
          bValue = (referenceData.markets || []).find(m => parseInt(m.id) === bMarketId)?.budget_market_name || '';
        }

        // Ay için özel sıralama
        if (sortConfig.key === 'budget_month') {
          // Boş değerleri kontrol et
          if ((!aValue || aValue === '' || aValue === null || aValue === undefined) &&
            (!bValue || bValue === '' || bValue === null || bValue === undefined)) {
            return 0;
          }
          if (!aValue || aValue === '' || aValue === null || aValue === undefined) return 1;
          if (!bValue || bValue === '' || bValue === null || bValue === undefined) return -1;

          // budget_month sayısal değer (1-12) olarak geliyor
          const aMonthNum = typeof aValue === 'number' ? aValue : (typeof aValue === 'string' ? monthOrder[aValue] || parseInt(aValue) || 999 : 999);
          const bMonthNum = typeof bValue === 'number' ? bValue : (typeof bValue === 'string' ? monthOrder[bValue] || parseInt(bValue) || 999 : 999);

          return sortConfig.direction === 'asc'
            ? aMonthNum - bMonthNum
            : bMonthNum - aMonthNum;
        }

        // Sayısal değerler için
        if (sortConfig.key === 'day_count' || sortConfig.key === 'pax' || sortConfig.key === 'pp') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        // String değerler için
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase().trim();
        }
        if (typeof bValue === 'string') {
          bValue = bValue.toLowerCase().trim();
        }

        // Boş değerleri en alta al
        if (aValue === '' || aValue === null || aValue === undefined) return 1;
        if (bValue === '' || bValue === null || bValue === undefined) return -1;

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });

      return sorted;
    };

    // Filtreleme mantığı - Filtre yoksa tüm veriler, varsa filtrelenmiş veriler
    let filteredRows = singleRowData;

    // Pazar filtresi
    if (!isAkkaBonus && marketFilter.length > 0) {
      filteredRows = filteredRows.filter((row) => {
        const rowMarketId = parseInt(row.market);
        const market = (referenceData.markets || []).find(m => parseInt(m.id) === rowMarketId);
        const marketName = market?.budget_market_name || '';
        return marketFilter.some(
          (filterMarket) => normalizeValue(marketName) === normalizeValue(filterMarket)
        );
      });
    }

    // Ülke filtresi
    if (!isAkkaBonus && countryFilter.length > 0) {
      filteredRows = filteredRows.filter((row) =>
        countryFilter.some(
          (country) => normalizeCountry(row.country) === normalizeCountry(country)
        )
      );
    }

    // Şehir filtresi
    if (!isAkkaBonus && cityFilter.length > 0) {
      filteredRows = filteredRows.filter((row) =>
        cityFilter.some(
          (city) => normalizeValue(row.city) === normalizeValue(city)
        )
      );
    }

    const rowsToDisplay = getSortedData(filteredRows);

    // Toplamları hesapla - Filtreleme yapılsın ya da yapılmasın her zaman çalışır
    // Filtre yoksa: tüm verilerin toplamı
    // Filtre varsa: filtrelenmiş verilerin toplamı
    const calculateTotals = () => {
      const totals = {
        subgroups: {},
        subgroupPax: {}, // AKKA Bonus için PAX toplamları
        grandTotal: 0
      };

      // rowsToDisplay zaten filtrelenmiş ve sıralanmış verileri içeriyor
      rowsToDisplay.forEach(row => {
        Object.entries(row.subgroupAmounts || {}).forEach(([subgroupId, amount]) => {
          const numericAmount = parseFloat(amount) || 0;
          if (!totals.subgroups[subgroupId]) {
            totals.subgroups[subgroupId] = 0;
          }
          totals.subgroups[subgroupId] += numericAmount;
          totals.grandTotal += numericAmount;
        });

        // PAX toplamları (AKKA Bonus için)
        if (isAkkaBonus) {
          Object.entries(row.subgroupPax || {}).forEach(([subgroupId, pax]) => {
            const numericPax = parseFloat(pax) || 0;
            if (!totals.subgroupPax[subgroupId]) {
              totals.subgroupPax[subgroupId] = 0;
            }
            totals.subgroupPax[subgroupId] += numericPax;
          });
        }
      });

      return totals;
    };

    // Toplamları hesapla - Her zaman çalışır (filtre olsun ya da olmasın)
    const totals = calculateTotals();

    // Sıralama ikonu render fonksiyonu
    const renderSortIcon = (columnKey) => {
      if (sortConfig.key !== columnKey) {
        return null;
      }
      return sortConfig.direction === 'asc'
        ? <ArrowUpIcon className="h-3 w-3 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        : <ArrowDownIcon className="h-3 w-3 text-blue-600 dark:text-blue-400 flex-shrink-0" />;
    };

    // Ülke filtre fonksiyonları
    const isCountrySelected = (country) =>
      countryFilter.some(
        (selected) => normalizeCountry(selected) === normalizeCountry(country)
      );
    const toggleCountrySelection = (country) => {
      const trimmed = (country || '').trim();
      if (!trimmed) return;
      setCountryFilter((prev) => {
        const exists = prev.some(
          (item) => normalizeCountry(item) === normalizeCountry(trimmed)
        );
        if (exists) {
          return prev.filter(
            (item) => normalizeCountry(item) !== normalizeCountry(trimmed)
          );
        }
        return [...prev, trimmed];
      });
    };
    const clearCountrySelection = () => {
      setCountryFilter([]);
    };
    const countryFilterLabel = () => {
      if (countryFilter.length === 0) return 'Tüm Ülkeler';
      if (countryFilter.length === 1) return countryFilter[0];
      return `${countryFilter.length} ülke seçili`;
    };

    // Pazar filtre fonksiyonları
    const isMarketSelected = (market) =>
      marketFilter.some(
        (selected) => normalizeValue(selected) === normalizeValue(market)
      );
    const toggleMarketSelection = (market) => {
      const trimmed = (market || '').trim();
      if (!trimmed) return;
      setMarketFilter((prev) => {
        const exists = prev.some(
          (item) => normalizeValue(item) === normalizeValue(trimmed)
        );
        if (exists) {
          return prev.filter(
            (item) => normalizeValue(item) !== normalizeValue(trimmed)
          );
        }
        return [...prev, trimmed];
      });
    };
    const clearMarketSelection = () => {
      setMarketFilter([]);
    };
    const marketFilterLabel = () => {
      if (marketFilter.length === 0) return 'Tüm Pazarlar';
      if (marketFilter.length === 1) return marketFilter[0];
      return `${marketFilter.length} pazar seçili`;
    };

    // Şehir filtre fonksiyonları
    const isCitySelected = (city) =>
      cityFilter.some(
        (selected) => normalizeValue(selected) === normalizeValue(city)
      );
    const toggleCitySelection = (city) => {
      const trimmed = (city || '').trim();
      if (!trimmed) return;
      setCityFilter((prev) => {
        const exists = prev.some(
          (item) => normalizeValue(item) === normalizeValue(trimmed)
        );
        if (exists) {
          return prev.filter(
            (item) => normalizeValue(item) !== normalizeValue(trimmed)
          );
        }
        return [...prev, trimmed];
      });
    };
    const clearCitySelection = () => {
      setCityFilter([]);
    };
    const cityFilterLabel = () => {
      if (cityFilter.length === 0) return 'Tüm Şehirler';
      if (cityFilter.length === 1) return cityFilter[0];
      return `${cityFilter.length} şehir seçili`;
    };

    // Filtrelenmiş seçenekler (arama için - baş harfe göre)
    const getFilteredMarketOptions = () => {
      if (!marketFilterSearch.trim()) return marketOptions;
      const searchTerm = marketFilterSearch.toLowerCase().trim();
      return marketOptions.filter(market =>
        market.toLowerCase().startsWith(searchTerm)
      );
    };

    const getFilteredCountryOptions = () => {
      if (!countryFilterSearch.trim()) return countryOptions;
      const searchTerm = countryFilterSearch.toLowerCase().trim();
      return countryOptions.filter(country =>
        country.toLowerCase().startsWith(searchTerm)
      );
    };

    const getFilteredCityOptions = () => {
      if (!cityFilterSearch.trim()) return cityOptions;
      const searchTerm = cityFilterSearch.toLowerCase().trim();
      return cityOptions.filter(city =>
        city.toLowerCase().startsWith(searchTerm)
      );
    };

    // Ana grup için alt grupları al
    const currentMaingroupId = parseInt(formData.maingroup);
    const availableSubgroupsRaw = (referenceData.subgroups || []).filter(subgroup =>
      parseInt(subgroup.maingroup) === currentMaingroupId
    );

    // AKKA Bonus için ayları kronolojik sıraya göre sırala
    const availableSubgroups = isAkkaBonus ? (() => {
      const monthOrder = {
        'Ocak': 1, 'Şubat': 2, 'Mart': 3, 'Nisan': 4, 'Mayıs': 5, 'Haziran': 6,
        'Temmuz': 7, 'Ağustos': 8, 'Eylül': 9, 'Ekim': 10, 'Kasım': 11, 'Aralık': 12
      };

      return [...availableSubgroupsRaw].sort((a, b) => {
        const aMonth = monthOrder[a.subgroup_name] || 999;
        const bMonth = monthOrder[b.subgroup_name] || 999;
        return aMonth - bMonth;
      });
    })() : availableSubgroupsRaw;

    // Çoklu satırdan çoklu kayıt oluştur
    const generateEntries = () => {
      const entries = [];

      singleRowData.forEach(row => {
        Object.entries(row.subgroupAmounts || {}).forEach(([subgroupId, amount]) => {
          // Boş string, null, undefined değilse ve geçerli bir sayıysa kaydet
          const numericAmount = parseFloat(amount);
          if (amount !== '' && amount !== null && amount !== undefined && !isNaN(numericAmount) && numericAmount >= 0) {
            const subgroup = availableSubgroups.find(sg => sg.id === parseInt(subgroupId));
            entries.push({
              id: `temp-${Date.now()}-${row.id}-${subgroupId}`,
              subgroup: subgroupId,
              subgroup_name: subgroup?.subgroup_name,
              market: row.market,
              country: row.country,
              city: row.city,
              agency: row.agency,
              hotel: row.hotel,
              start_date: row.start_date,
              end_date: row.end_date,
              pax: row.pax,
              pp: row.pp,
              day_count: row.day_count,
              summary: row.summary,
              budget_amount: numericAmount,
              budget_month: row.budget_month
            });
          }
        });
      });

      return entries;
    };



    return (
      <div className="space-y-3 md:space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Button
              type="button"
              onClick={addNewRow}
              className="h-8 px-3 bg-blue-600 hover:bg-blue-700 text-sm text-white"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Satır Ekle
            </Button>
            {!isAkkaBonus && (
              <>
                {/* Pazar Filtresi */}
                <div
                  className="relative flex items-center space-x-2"
                  ref={marketFilterDropdownRef}
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pazar:
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsMarketFilterDropdownOpen((prev) => !prev)}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 flex items-center space-x-2"
                  >
                    <span>{marketFilterLabel()}</span>
                    <svg
                      className={`h-4 w-4 transition-transform ${isMarketFilterDropdownOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isMarketFilterDropdownOpen && (
                    <div className="absolute top-full mt-2 left-0 z-[10000] w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                          Pazarlar
                        </span>
                        <Button
                          type="button"
                          onClick={clearMarketSelection}
                          variant="link"
                          className="h-auto p-0 text-xs text-blue-600 dark:text-blue-400"
                        >
                          Temizle
                        </Button>
                      </div>
                      <input
                        type="text"
                        placeholder="Pazar ara..."
                        value={marketFilterSearch}
                        onChange={(e) => setMarketFilterSearch(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {getFilteredMarketOptions().length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {marketFilterSearch.trim() ? 'Sonuç bulunamadı' : 'Henüz pazar girilmedi.'}
                        </p>
                      ) : (
                        getFilteredMarketOptions().map((market) => (
                          <button
                            key={market}
                            type="button"
                            onClick={() => toggleMarketSelection(market)}
                            className={`w-full flex items-center justify-between px-2 py-1 rounded-md text-sm transition-colors ${isMarketSelected(market)
                              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                          >
                            <span>{market}</span>
                            {isMarketSelected(market) && (
                              <CheckIcon className="h-4 w-4 text-green-500" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Ülke Filtresi */}
                <div
                  className="relative flex items-center space-x-2"
                  ref={countryFilterDropdownRef}
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Ülke:
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsCountryFilterDropdownOpen((prev) => !prev)}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 flex items-center space-x-2"
                  >
                    <span>{countryFilterLabel()}</span>
                    <svg
                      className={`h-4 w-4 transition-transform ${isCountryFilterDropdownOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isCountryFilterDropdownOpen && (
                    <div className="absolute top-full mt-2 left-0 z-[10000] w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                          Ülkeler
                        </span>
                        <Button
                          type="button"
                          onClick={clearCountrySelection}
                          variant="link"
                          className="h-auto p-0 text-xs text-blue-600 dark:text-blue-400"
                        >
                          Temizle
                        </Button>
                      </div>
                      <input
                        type="text"
                        placeholder="Ülke ara..."
                        value={countryFilterSearch}
                        onChange={(e) => setCountryFilterSearch(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {getFilteredCountryOptions().length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {countryFilterSearch.trim() ? 'Sonuç bulunamadı' : 'Henüz ülke girilmedi.'}
                        </p>
                      ) : (
                        getFilteredCountryOptions().map((country) => (
                          <button
                            key={country}
                            type="button"
                            onClick={() => toggleCountrySelection(country)}
                            className={`w-full flex items-center justify-between px-2 py-1 rounded-md text-sm transition-colors ${isCountrySelected(country)
                              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                          >
                            <span>{country}</span>
                            {isCountrySelected(country) && (
                              <CheckIcon className="h-4 w-4 text-green-500" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Şehir Filtresi */}
                <div
                  className="relative flex items-center space-x-2"
                  ref={cityFilterDropdownRef}
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Şehir:
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsCityFilterDropdownOpen((prev) => !prev)}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 flex items-center space-x-2"
                  >
                    <span>{cityFilterLabel()}</span>
                    <svg
                      className={`h-4 w-4 transition-transform ${isCityFilterDropdownOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isCityFilterDropdownOpen && (
                    <div className="absolute top-full mt-2 left-0 z-[10000] w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                          Şehirler
                        </span>
                        <Button
                          type="button"
                          onClick={clearCitySelection}
                          variant="link"
                          className="h-auto p-0 text-xs text-blue-600 dark:text-blue-400"
                        >
                          Temizle
                        </Button>
                      </div>
                      <input
                        type="text"
                        placeholder="Şehir ara..."
                        value={cityFilterSearch}
                        onChange={(e) => setCityFilterSearch(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {getFilteredCityOptions().length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {cityFilterSearch.trim() ? 'Sonuç bulunamadı' : 'Henüz şehir girilmedi.'}
                        </p>
                      ) : (
                        getFilteredCityOptions().map((city) => (
                          <button
                            key={city}
                            type="button"
                            onClick={() => toggleCitySelection(city)}
                            className={`w-full flex items-center justify-between px-2 py-1 rounded-md text-sm transition-colors ${isCitySelected(city)
                              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                          >
                            <span>{city}</span>
                            {isCitySelected(city) && (
                              <CheckIcon className="h-4 w-4 text-green-500" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Excel Benzeri Tablo */}
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 min-w-[1500px]">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-20 shadow-lg border-b-2 border-gray-300 dark:border-gray-600">
              <th rowSpan={isAkkaBonus ? 2 : 1} className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs font-medium text-gray-900 dark:text-white min-w-[50px] bg-gray-50 dark:bg-gray-700">
                #
              </th>
              {isAkkaBonus && (
                <th rowSpan="2" className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs font-medium text-gray-900 dark:text-white min-w-[150px] bg-gray-50 dark:bg-gray-700">
                  Otel Adı
                </th>
              )}
              {!isAkkaBonus && (
                <>
                  <th
                    onClick={() => handleSort('market')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[120px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Pazar *</span>
                      {renderSortIcon('market')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('country')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Ülke</span>
                      {renderSortIcon('country')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('city')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Şehir</span>
                      {renderSortIcon('city')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('agency')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[120px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Acenta</span>
                      {renderSortIcon('agency')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('hotel')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[120px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Otel</span>
                      {renderSortIcon('hotel')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('summary')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[150px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Özet</span>
                      {renderSortIcon('summary')}
                    </div>
                  </th>
                </>
              )}
              {/* Sales Call için özel alanlar */}
              {selectedGroup?.maingroup_name === 'Sales Call' && (
                <>
                  <th
                    onClick={() => handleSort('day_count')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Gün Sayısı</span>
                      {renderSortIcon('day_count')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('pax')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Kişi Sayısı</span>
                      {renderSortIcon('pax')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('pp')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[80px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">PP</span>
                      {renderSortIcon('pp')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('budget_month')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Ay</span>
                      {renderSortIcon('budget_month')}
                    </div>
                  </th>
                </>
              )}
              {/* Temsil Ağırlama için özel alanlar */}
              {isTemsilAgir && (
                <>
                  <th
                    onClick={() => handleSort('pax')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">PAX</span>
                      {renderSortIcon('pax')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('budget_month')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Ay</span>
                      {renderSortIcon('budget_month')}
                    </div>
                  </th>
                </>
              )}
              {/* Dijital Pazarlama için özel alanlar */}
              {isDijitalPazarlama && (
                <>
                  <th
                    onClick={() => handleSort('region')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[120px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Bölge</span>
                      {renderSortIcon('region')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('eur_curr')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Euro Kur</span>
                      {renderSortIcon('eur_curr')}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('budget_month')}
                    className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <span className="whitespace-nowrap">Ay</span>
                      {renderSortIcon('budget_month')}
                    </div>
                  </th>
                </>
              )}
              {isAkkaBonus ? (
                <>
                  {availableSubgroups.map((subgroup) => (
                    <th key={subgroup.id} colSpan="2" className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs font-medium text-gray-900 dark:text-white bg-green-100 dark:bg-green-800">
                      {(subgroup.subgroup_name || '').toUpperCase()}
                    </th>
                  ))}
                  <th rowSpan="2" className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs font-medium text-gray-900 dark:text-white min-w-[80px] bg-green-50 dark:bg-green-900/20">
                    Toplam
                  </th>
                  <th rowSpan="2" className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs font-medium text-gray-900 dark:text-white min-w-[80px] bg-gray-50 dark:bg-gray-700">
                    İşlemler
                  </th>
                </>
              ) : (
                <>
                  {availableSubgroups.map((subgroup) => (
                    <th key={subgroup.id} className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-sm font-medium text-gray-900 dark:text-white min-w-[120px] bg-gray-50 dark:bg-gray-700">
                      {subgroup.subgroup_name}
                    </th>
                  ))}
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-sm font-medium text-gray-900 dark:text-white min-w-[120px] bg-green-50 dark:bg-green-900/20">
                    Toplam
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-sm font-medium text-gray-900 dark:text-white min-w-[100px] bg-gray-50 dark:bg-gray-700">
                    İşlemler
                  </th>
                </>
              )}
            </tr>
            {isAkkaBonus && (
              <tr className="bg-green-100 dark:bg-green-800 sticky top-0 z-20 shadow-lg border-b-2 border-gray-300 dark:border-gray-600">
                {availableSubgroups.map((subgroup) => (
                  <React.Fragment key={subgroup.id}>
                    <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center text-xs font-medium text-gray-900 dark:text-white min-w-[65px] bg-green-100 dark:bg-green-800">
                      PAX
                    </th>
                    <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center text-xs font-medium text-gray-900 dark:text-white min-w-[100px] bg-green-100 dark:bg-green-800">
                      TUTAR
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {rowsToDisplay.map((row, index) => (
              <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                {/* Satır Numarası */}
                <td className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700">
                  {index + 1}
                </td>

                {/* AKKA Bonus için Otel Adı */}
                {isAkkaBonus && (
                  <td className="border border-gray-300 dark:border-gray-600 px-2 py-1.5">
                    <div className="relative">
                      <input
                        id={`hotel-input-akka-${row.id}`}
                        type="text"
                        value={row.hotel || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          updateRowData(row.id, 'hotel', val);
                          setHotelSearchTerm(prev => ({ ...prev, [row.id]: val }));
                          setShowHotelDropdown(prev => ({ ...prev, [row.id]: true }));
                        }}
                        onFocus={() => setShowHotelDropdown(prev => ({ ...prev, [row.id]: true }))}
                        placeholder="Otel ara veya seç..."
                        className="w-full px-2 py-1 pr-8 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                      />
                      <button
                        type="button"
                        onClick={() => setIsAddingNewHotel(true)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        title="Yeni Otel Ekle"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                      {showHotelDropdown[row.id] && (
                        <div id={`hotel-dropdown-akka-${row.id}`} className="absolute z-[9999] w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {getFilteredHotels(row.id).length > 0 ? (
                            getFilteredHotels(row.id).map((hotel) => (
                              <div
                                key={hotel.id}
                                onClick={() => {
                                  updateRowData(row.id, 'hotel', hotel.hotel_name);
                                  setHotelSearchTerm(prev => ({ ...prev, [row.id]: '' }));
                                  setShowHotelDropdown(prev => ({ ...prev, [row.id]: false }));
                                }}
                                className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-xs text-gray-900 dark:text-white"
                              >
                                {hotel.hotel_name}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                              Otel bulunamadı
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                )}

                {!isAkkaBonus && (
                  <>
                    {/* Pazar */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <div className="relative">
                        <input
                          id={`market-input-${row.id}`}
                          type="text"
                          value={(referenceData.markets || []).find(m => parseInt(m.id) === parseInt(row.market))?.budget_market_name || ''}
                          onChange={(e) => {
                            setMarketSearchTerm(prev => ({ ...prev, [row.id]: e.target.value }));
                            setShowMarketDropdown(prev => ({ ...prev, [row.id]: true }));
                          }}
                          onFocus={() => setShowMarketDropdown(prev => ({ ...prev, [row.id]: true }))}
                          placeholder="Pazar ara veya seç..."
                          className="w-full px-2 py-1 pr-8 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setIsAddingNewMarket(true)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                          title="Yeni Pazar Ekle"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                        {showMarketDropdown[row.id] && (
                          <div id={`market-dropdown-${row.id}`} className="absolute z-[9999] w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto min-w-[200px]">
                            {getFilteredMarkets(row.id).length > 0 ? (
                              getFilteredMarkets(row.id).map((market) => (
                                <div
                                  key={market.id}
                                  onClick={() => {
                                    updateRowData(row.id, 'market', market.id);
                                    setMarketSearchTerm(prev => ({ ...prev, [row.id]: '' }));
                                    setShowMarketDropdown(prev => ({ ...prev, [row.id]: false }));
                                  }}
                                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-white"
                                >
                                  {market.budget_market_name}
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                Pazar bulunamadı
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Ülke */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <div className="relative">
                        <input
                          id={`country-input-${row.id}`}
                          type="text"
                          value={row.country || ''}
                          onChange={(e) => {
                            updateRowData(row.id, 'country', e.target.value);
                            setCountrySearchTerm(prev => ({ ...prev, [row.id]: e.target.value }));
                            setShowCountryDropdown(prev => ({ ...prev, [row.id]: true }));
                          }}
                          onFocus={() => setShowCountryDropdown(prev => ({ ...prev, [row.id]: true }))}
                          placeholder="Ülke ara veya seç..."
                          className="w-full px-2 py-1 pr-8 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setIsAddingNewCountry(true)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                          title="Yeni Ülke Ekle"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                        {showCountryDropdown[row.id] && (
                          <div id={`country-dropdown-${row.id}`} className="absolute z-[9999] w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {getFilteredCountries(row.id).length > 0 ? (
                              getFilteredCountries(row.id).map((country) => (
                                <div
                                  key={country.id}
                                  onClick={() => {
                                    updateRowData(row.id, 'country', country.country_name);
                                    setCountrySearchTerm(prev => ({ ...prev, [row.id]: '' }));
                                    setShowCountryDropdown(prev => ({ ...prev, [row.id]: false }));
                                  }}
                                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-white"
                                >
                                  {country.country_name}
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                Ülke bulunamadı
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Şehir */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <div className="relative">
                        <input
                          id={`city-input-${row.id}`}
                          type="text"
                          value={row.city || ''}
                          onChange={(e) => {
                            updateRowData(row.id, 'city', e.target.value);
                            setCitySearchTerm(prev => ({ ...prev, [row.id]: e.target.value }));
                            setShowCityDropdown(prev => ({ ...prev, [row.id]: true }));
                          }}
                          onFocus={() => setShowCityDropdown(prev => ({ ...prev, [row.id]: true }))}
                          placeholder="Şehir ara veya seç..."
                          className="w-full px-2 py-1 pr-8 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setIsAddingNewCity(true)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                          title="Yeni Şehir Ekle"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                        {showCityDropdown[row.id] && (
                          <div id={`city-dropdown-${row.id}`} className="absolute z-[9999] w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {getFilteredCities(row.id).length > 0 ? (
                              getFilteredCities(row.id).map((city) => (
                                <div
                                  key={city.id}
                                  onClick={() => {
                                    updateRowData(row.id, 'city', city.city_name);
                                    setCitySearchTerm(prev => ({ ...prev, [row.id]: '' }));
                                    setShowCityDropdown(prev => ({ ...prev, [row.id]: false }));
                                  }}
                                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-white"
                                >
                                  {city.city_name}
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                Şehir bulunamadı
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Acenta */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <div className="relative">
                        <input
                          id={`agency-input-${row.id}`}
                          type="text"
                          value={row.agency || ''}
                          onChange={(e) => {
                            updateRowData(row.id, 'agency', e.target.value);
                            setAgencySearchTerm(prev => ({ ...prev, [row.id]: e.target.value }));
                            setShowAgencyDropdown(prev => ({ ...prev, [row.id]: true }));
                          }}
                          onFocus={() => setShowAgencyDropdown(prev => ({ ...prev, [row.id]: true }))}
                          placeholder="Acenta ara veya seç..."
                          className="w-full px-2 py-1 pr-8 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setIsAddingNewAgency(true)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                          title="Yeni Acenta Ekle"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                        {showAgencyDropdown[row.id] && (
                          <div id={`agency-dropdown-${row.id}`} className="absolute z-[9999] w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto" style={{ bottom: '100%', marginBottom: '4px' }}>
                            {getFilteredAgencies(row.id).length > 0 ? (
                              getFilteredAgencies(row.id).map((agency) => (
                                <div
                                  key={agency.id}
                                  onClick={() => {
                                    updateRowData(row.id, 'agency', agency.agency_name);
                                    setAgencySearchTerm(prev => ({ ...prev, [row.id]: '' }));
                                    setShowAgencyDropdown(prev => ({ ...prev, [row.id]: false }));
                                  }}
                                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-white"
                                >
                                  {agency.agency_name}
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                Acenta bulunamadı
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Otel */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <div className="relative">
                        <input
                          id={`hotel-input-${row.id}`}
                          type="text"
                          value={row.hotel || ''}
                          onChange={(e) => {
                            updateRowData(row.id, 'hotel', e.target.value);
                            setHotelSearchTerm(prev => ({ ...prev, [row.id]: e.target.value }));
                            setShowHotelDropdown(prev => ({ ...prev, [row.id]: true }));
                          }}
                          onFocus={() => setShowHotelDropdown(prev => ({ ...prev, [row.id]: true }))}
                          placeholder="Otel ara veya seç..."
                          className="w-full px-2 py-1 pr-8 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setIsAddingNewHotel(true)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                          title="Yeni Otel Ekle"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                        {showHotelDropdown[row.id] && (
                          <div id={`hotel-dropdown-${row.id}`} className="absolute z-[9999] w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {getFilteredHotels(row.id).length > 0 ? (
                              getFilteredHotels(row.id).map((hotel) => (
                                <div
                                  key={hotel.id}
                                  onClick={() => {
                                    updateRowData(row.id, 'hotel', hotel.hotel_name);
                                    setHotelSearchTerm(prev => ({ ...prev, [row.id]: '' }));
                                    setShowHotelDropdown(prev => ({ ...prev, [row.id]: false }));
                                  }}
                                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-white"
                                >
                                  {hotel.hotel_name}
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                Otel bulunamadı
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Özet */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <input
                        type="text"
                        value={row.summary || ''}
                        onChange={(e) => updateRowData(row.id, 'summary', e.target.value)}
                        placeholder="Özet"
                        maxLength="100"
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </td>
                  </>
                )}

                {/* Sales Call için özel alanlar */}
                {selectedGroup?.maingroup_name === 'Sales Call' && (
                  <>
                    {/* Gün Sayısı */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <input
                        type="number"
                        value={row.day_count || ''}
                        onChange={(e) => updateRowData(row.id, 'day_count', parseInt(e.target.value) || 0)}
                        placeholder="Gün"
                        min="0"
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-center"
                      />
                    </td>

                    {/* Kişi Sayısı */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <input
                        type="number"
                        value={row.pax || ''}
                        onChange={(e) => updateRowData(row.id, 'pax', parseInt(e.target.value) || 0)}
                        placeholder="Kişi"
                        min="0"
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-center"
                      />
                    </td>

                    {/* PP */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <input
                        type="number"
                        value={row.pp || ''}
                        onChange={(e) => updateRowData(row.id, 'pp', parseInt(e.target.value) || 0)}
                        placeholder="PP"
                        min="0"
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-center"
                      />
                    </td>

                    {/* Ay */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <select
                        value={row.budget_month || ''}
                        onChange={(e) => updateRowData(row.id, 'budget_month', parseInt(e.target.value) || null)}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="">Ay Seçin</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                          <option key={month} value={month}>
                            {new Date(2024, month - 1).toLocaleString('tr-TR', { month: 'long' })}
                          </option>
                        ))}
                      </select>
                    </td>
                  </>
                )}

                {/* Temsil Ağırlama için özel alanlar */}
                {isTemsilAgir && (
                  <>
                    {/* PAX */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <input
                        type="number"
                        value={row.pax || ''}
                        onChange={(e) => updateRowData(row.id, 'pax', parseInt(e.target.value) || 0)}
                        placeholder="PAX"
                        min="0"
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-center"
                      />
                    </td>

                    {/* Ay */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <select
                        value={row.budget_month || ''}
                        onChange={(e) => updateRowData(row.id, 'budget_month', parseInt(e.target.value) || null)}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="">Ay Seçin</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                          <option key={month} value={month}>
                            {new Date(2024, month - 1).toLocaleString('tr-TR', { month: 'long' })}
                          </option>
                        ))}
                      </select>
                    </td>
                  </>
                )}

                {/* Dijital Pazarlama için özel alanlar */}
                {isDijitalPazarlama && (
                  <>
                    {/* Bölge */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <input
                        type="text"
                        value={row.region || ''}
                        onChange={(e) => updateRowData(row.id, 'region', e.target.value)}
                        placeholder="Bölge"
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </td>

                    {/* Euro Kur */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <input
                        type="number"
                        value={row.eur_curr || ''}
                        onChange={(e) => updateRowData(row.id, 'eur_curr', parseFloat(e.target.value) || 0)}
                        placeholder="Euro Kur"
                        step="0.01"
                        min="0"
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-center"
                      />
                    </td>

                    {/* Ay */}
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                      <select
                        value={row.budget_month || ''}
                        onChange={(e) => updateRowData(row.id, 'budget_month', parseInt(e.target.value) || null)}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="">Ay Seçin</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                          <option key={month} value={month}>
                            {new Date(2024, month - 1).toLocaleString('tr-TR', { month: 'long' })}
                          </option>
                        ))}
                      </select>
                    </td>
                  </>
                )}

                {/* Alt Grup Tutarları */}
                {isAkkaBonus ? (
                  <>
                    {availableSubgroups.map((subgroup) => (
                      <React.Fragment key={subgroup.id}>
                        {/* PAX */}
                        <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1.5">
                          <input
                            type="number"
                            value={row.subgroupPax?.[subgroup.id] || ''}
                            onChange={(e) => updateSubgroupPax(row.id, subgroup.id, e.target.value)}
                            placeholder="-"
                            min="0"
                            className="w-full px-1.5 py-0.5 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 text-xs text-center font-medium"
                          />
                        </td>
                        {/* TUTAR */}
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-1.5">
                          <input
                            type="number"
                            value={row.subgroupAmounts?.[subgroup.id] || ''}
                            onChange={(e) => updateSubgroupAmount(row.id, subgroup.id, e.target.value)}
                            placeholder="-"
                            step="0.01"
                            min="0"
                            className="w-full px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-green-600 dark:text-green-400 text-xs text-center font-medium"
                          />
                        </td>
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  <>
                    {availableSubgroups.map((subgroup) => (
                      <td key={subgroup.id} className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                        <input
                          type="number"
                          value={row.subgroupAmounts?.[subgroup.id] || ''}
                          onChange={(e) => updateSubgroupAmount(row.id, subgroup.id, e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-center"
                        />
                      </td>
                    ))}
                  </>
                )}

                {/* Toplam */}
                <td className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
                  €{Object.values(row.subgroupAmounts || {})
                    .reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0)
                    .toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </td>

                {/* İşlemler */}
                <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center">
                  <div className="flex justify-center space-x-1">
                    <button
                      onClick={() => duplicateRow(row.id)}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      title="Çoğalt"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removeRow(row.id)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      title="Sil"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {/* Dip Toplam Satırı */}
            <tr className="bg-gray-100 dark:bg-gray-800 font-bold">
              <td className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs text-gray-900 dark:text-white">
                Toplam
              </td>
              {/* Toplam Satıra kadar olan boşluklar */}
              {(() => {
                let colSpan = 0;

                if (isAkkaBonus) {
                  // AKKA Bonus için: (Sıra No zaten render edildi), Otel Adı
                  colSpan = 1;
                } else {
                  // Diğerleri için: (Sıra No zaten render edildi) 
                  // Pazar, Ülke, Şehir, Acenta, Otel, Özet
                  colSpan = 6;

                  if (selectedGroup?.maingroup_name === 'Sales Call') {
                    colSpan += 4; // Gün Sayısı, Kişi Sayısı, PP, Ay
                  } else if (isTemsilAgir) {
                    colSpan += 2; // PAX, Ay
                  } else if (isDijitalPazarlama) {
                    colSpan += 3; // Bölge, Euro Kur, Ay
                  }
                }

                return colSpan > 0 ? (
                  <td colSpan={colSpan} className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs text-gray-900 dark:text-white"></td>
                ) : null;
              })()}

              {/* Alt Grup Toplamları */}
              {isAkkaBonus ? (
                <>
                  {availableSubgroups.map((subgroup) => (
                    <React.Fragment key={subgroup.id}>
                      {/* PAX Toplam */}
                      <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1.5 text-center text-xs font-bold text-blue-600 dark:text-blue-400 bg-yellow-50 dark:bg-yellow-900/20">
                        {totals.subgroupPax[subgroup.id] || '-'}
                      </td>
                      {/* TUTAR Toplam */}
                      <td className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs font-bold text-green-600 dark:text-green-400 bg-yellow-50 dark:bg-yellow-900/20">
                        €{(totals.subgroups[subgroup.id] || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </td>
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <>
                  {availableSubgroups.map((subgroup) => (
                    <td key={subgroup.id} className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-sm font-bold text-gray-900 dark:text-white bg-yellow-50 dark:bg-yellow-900/20">
                      €{(totals.subgroups[subgroup.id] || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </td>
                  ))}
                </>
              )}

              {/* Genel Toplam */}
              <td className="border border-gray-300 dark:border-gray-600 px-2 py-1.5 text-center text-xs font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
                €{totals.grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </td>

              {/* İşlemler kolonu boş */}
              <td className="border border-gray-300 dark:border-gray-600 px-2 py-1.5"></td>
            </tr>
          </tbody>
        </table>


      </div>
    );
  };

  // Field input renderer
  const renderFieldInput = (entry, field, fieldConfig) => {
    const isRequired = fieldConfig.requiredFields.includes(field);

    switch (field) {
      case 'subgroup':
        return (
          <select
            value={entry.subgroup || ''}
            onChange={(e) => updateEntry(entry.id, 'subgroup', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm ${isRequired && !entry.subgroup ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
              }`}
          >
            <option value="">Alt Grup Seçin</option>
            {referenceData.subgroups
              .filter(subgroup => subgroup.maingroup === parseInt(formData.maingroup))
              .map((subgroup) => (
                <option key={subgroup.id} value={subgroup.id}>
                  {subgroup.subgroup_name}
                </option>
              ))}
          </select>
        );

      case 'market':
        return (
          <select
            value={entry.market || ''}
            onChange={(e) => updateEntry(entry.id, 'market', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm ${isRequired && !entry.market ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
              }`}
          >
            <option value="">Pazar Seçin</option>
            {referenceData.markets.map((market) => (
              <option key={market.id} value={market.id}>
                {market.budget_market_name}
              </option>
            ))}
          </select>
        );

      case 'country':
        return (
          <select
            value={entry.country || ''}
            onChange={(e) => updateEntry(entry.id, 'country', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">Ülke Seçin</option>
            {referenceData.countries.map((country) => (
              <option key={country.id} value={country.country_name}>
                {country.country_name}
              </option>
            ))}
          </select>
        );

      case 'city':
        return (
          <input
            type="text"
            value={entry.city || ''}
            onChange={(e) => updateEntry(entry.id, 'city', e.target.value)}
            placeholder="Şehir girin"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        );

      case 'agency':
        return (
          <select
            value={entry.agency || ''}
            onChange={(e) => updateEntry(entry.id, 'agency', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">Acenta Seçin</option>
            {referenceData.agencies.map((agency) => (
              <option key={agency.id} value={agency.agency_name}>
                {agency.agency_name}
              </option>
            ))}
          </select>
        );

      case 'budget_amount':
        return (
          <input
            type="number"
            value={entry.budget_amount || ''}
            onChange={(e) => updateEntry(entry.id, 'budget_amount', e.target.value)}
            placeholder="Tutar girin"
            step="0.01"
            min="0"
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm ${isRequired && (!entry.budget_amount || parseFloat(entry.budget_amount) <= 0) ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
              }`}
          />
        );

      case 'summary':
        return (
          <input
            type="text"
            value={entry.summary || ''}
            onChange={(e) => updateEntry(entry.id, 'summary', e.target.value)}
            placeholder="Özet girin"
            maxLength="200"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        );

      case 'start_date':
      case 'end_date':
        return (
          <input
            type="date"
            value={entry[field] || ''}
            onChange={(e) => updateEntry(entry.id, field, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        );

      case 'pax':
      case 'day_count':
        return (
          <input
            type="number"
            value={entry[field] || ''}
            onChange={(e) => updateEntry(entry.id, field, parseInt(e.target.value) || 0)}
            placeholder="Sayı girin"
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        );

      case 'pp':
        return (
          <input
            type="number"
            value={entry.pp || ''}
            onChange={(e) => updateEntry(entry.id, 'pp', parseFloat(e.target.value) || 0)}
            placeholder="PP tutarı"
            step="0.01"
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        );

      case 'budget_month':
        return (
          <select
            value={entry.budget_month || ''}
            onChange={(e) => updateEntry(entry.id, 'budget_month', parseInt(e.target.value) || null)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">Ay Seçin</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
              <option key={month} value={month}>
                {new Date(2024, month - 1).toLocaleString('tr-TR', { month: 'long' })}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={entry[field] || ''}
            onChange={(e) => updateEntry(entry.id, field, e.target.value)}
            placeholder={`${fieldConfig.fieldLabels[field] || field} girin`}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        );
    }
  };

  if (loading && (!referenceData.maingroups || !referenceData.maingroups.length)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Ana Sayfa - Budget ekranının aynısı
  if (!showDataEntry) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 md:py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Basic Budget Management
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Basit bütçe yönetimi ve takibi
            </p>
          </div>

          {/* Bütçe Kayıtları Özeti - Budget ekranının aynısı */}
          <BudgetSummaryCards
            onEdit={(entries) => {
              // Düzenleme için veri giriş ekranına geç ve veriyi yükle
              try {
                if (entries && entries.length > 0) {
                  const firstEntry = entries[0];

                  // Robust maingroup extraction
                  let entryMaingroupVal = firstEntry.maingroup_id || firstEntry.maingroup;
                  let entryMaingroupId = parseInt(entryMaingroupVal);
                  let entryMaingroupName = typeof entryMaingroupVal === 'string' ? entryMaingroupVal : null;

                  // If maingroup is an object
                  if (typeof firstEntry.maingroup === 'object' && firstEntry.maingroup !== null) {
                    entryMaingroupId = parseInt(firstEntry.maingroup.id);
                    entryMaingroupName = firstEntry.maingroup.name || firstEntry.maingroup.maingroup_name;
                  }

                  const selectedGroup = (referenceData.maingroups || []).find(g =>
                    (entryMaingroupId && parseInt(g.id) === entryMaingroupId) ||
                    (entryMaingroupName && g.maingroup_name === entryMaingroupName)
                  );

                  // Formdata için ID kullan - String'e çevir (<select> uyumluluğu için)
                  let finalSelectedGroup = selectedGroup;

                  if (!finalSelectedGroup) {
                    finalSelectedGroup = (referenceData.maingroups || []).find(g =>
                      g.maingroup_name === firstEntry.maingroup_name
                    );
                    if (finalSelectedGroup) {
                      console.log('Fallback maingroup found by name:', finalSelectedGroup.maingroup_name);
                    }
                  }

                  const maingroupIdToSet = finalSelectedGroup ? String(finalSelectedGroup.id) : (!isNaN(entryMaingroupId) && entryMaingroupId ? String(entryMaingroupId) : '');

                  // Robust budgettype extraction
                  let entryBudgetTypeVal = firstEntry.budgettype_id || firstEntry.budgettype;
                  let entryBudgetTypeId = parseInt(entryBudgetTypeVal);
                  let entryBudgetTypeName = typeof entryBudgetTypeVal === 'string' ? entryBudgetTypeVal : (firstEntry.budgettype_name || null);

                  if (typeof firstEntry.budgettype === 'object' && firstEntry.budgettype !== null) {
                    entryBudgetTypeId = parseInt(firstEntry.budgettype.id);
                    entryBudgetTypeName = firstEntry.budgettype.budget_type;
                  }

                  let selectedType = (referenceData.types || []).find(t =>
                    (entryBudgetTypeId && !isNaN(entryBudgetTypeId) && parseInt(t.id) === entryBudgetTypeId) ||
                    (entryBudgetTypeName && t.budget_type === entryBudgetTypeName)
                  );

                  // budgettype_name ile fallback dene
                  if (!selectedType && firstEntry.budgettype_name) {
                    selectedType = (referenceData.types || []).find(t =>
                      t.budget_type === firstEntry.budgettype_name
                    );
                  }

                  // String'e çevir (<select> uyumluluğu için)
                  const budgetTypeIdToSet = selectedType ? String(selectedType.id) : (entryBudgetTypeId && !isNaN(entryBudgetTypeId) ? String(entryBudgetTypeId) : '');

                  // Form verilerini yükle - API'den gelen field isimlerini kullan
                  setFormData({
                    maingroup: maingroupIdToSet,
                    budgettype: budgetTypeIdToSet,
                    year: firstEntry.budget_year || new Date().getFullYear(),
                    month: firstEntry.budget_month || null,
                    entries: entries
                  });
                  const isRepresHos = finalSelectedGroup?.maingroup_name === 'Temsil Ağırlama';
                  const isDijitalPazarlama = finalSelectedGroup?.maingroup_name === 'Dijital Pazarlama';
                  const isAkkaBonus = finalSelectedGroup?.maingroup_name?.includes('AKKA Bonus') || false;

                  const groupedEntries = {};

                  entries.forEach((entry) => {
                    // Gruplama anahtarı oluştur - Temsil Ağırlama / AKKA Bonus / Dijital Pazarlama için ay da dahil

                    // Robust safe-access helpers
                    const getVal = (val, key) => (val && typeof val === 'object' ? (val[key] || val.id || '') : (val || ''));

                    // AKKA Bonus için otel adına göre grupla
                    let groupKey;
                    if (isDijitalPazarlama) {
                      groupKey = `${getVal(entry.market, 'budget_market_name')}-${getVal(entry.country, 'country_name')}-${getVal(entry.city, 'city_name')}-${getVal(entry.agency, 'agency_name')}-${entry.region || ''}-${entry.eur_curr || 0}-${entry.budget_month || 'no-month'}`;
                    } else if (isAkkaBonus) {
                      // AKKA Bonus için otel adına göre grupla
                      groupKey = `${getVal(entry.hotel, 'hotel_name')}`;
                    } else if (isRepresHos) {
                      groupKey = `${getVal(entry.market, 'budget_market_name')}-${getVal(entry.country, 'country_name')}-${getVal(entry.city, 'city_name')}-${getVal(entry.agency, 'agency_name')}-${entry.budget_month || 'no-month'}`;
                    } else {
                      groupKey = `${getVal(entry.market, 'budget_market_name')}-${getVal(entry.country, 'country_name')}-${getVal(entry.city, 'city_name')}-${getVal(entry.agency, 'agency_name')}`;
                    }

                    if (!groupedEntries[groupKey]) {
                      groupedEntries[groupKey] = {
                        market: entry.market_id || getVal(entry.market, 'id'),
                        country: entry.country || getVal(entry.country, 'country_name'),
                        city: entry.city || getVal(entry.city, 'city_name'),
                        agency: entry.agency || getVal(entry.agency, 'agency_name'),
                        hotel: entry.hotel_name || getVal(entry.hotel, 'hotel_name'),
                        start_date: entry.start_date || '',
                        end_date: entry.end_date || '',
                        pax: entry.pax || '',
                        pp: entry.pp || '',
                        day_count: entry.day_count || '',
                        summary: entry.summary || '',
                        region: entry.region || '',
                        eur_curr: entry.eur_curr || 0,
                        budget_month: entry.budget_month || null,
                        subgroupAmounts: {},
                        subgroupPax: {}, // AKKA Bonus için PAX verileri
                        originalEntries: []
                      };
                    }

                    // Alt grup tutarını ekle
                    const subgroupId = entry.subgroup_id || getVal(entry.subgroup, 'id');
                    const budgetAmount = entry.budget_amount || entry.amount || entry.tutar;

                    if (subgroupId && budgetAmount) {
                      groupedEntries[groupKey].subgroupAmounts[subgroupId] = budgetAmount;
                    }

                    // AKKA Bonus ve Temsil Ağırlama için PAX değerini ekle
                    if ((isAkkaBonus || isRepresHos) && subgroupId && entry.pax) {
                      groupedEntries[groupKey].subgroupPax[subgroupId] = entry.pax;
                    }

                    // Temsil Ağırlama için: Eğer bu alt grup için PAX değeri yoksa, genel PAX değerini kullan
                    // (Temsil Ağırlama'da her alt grup için ayrı PAX olmayabilir, satır başına bir PAX olabilir)
                    if (isRepresHos && subgroupId && !groupedEntries[groupKey].subgroupPax[subgroupId] && entry.pax) {
                      groupedEntries[groupKey].subgroupPax[subgroupId] = entry.pax;
                    }

                    // Temsil Ağırlama için: Genel PAX değerini güncelle (en son entry'nin PAX değerini al)
                    if (isRepresHos && entry.pax) {
                      groupedEntries[groupKey].pax = entry.pax;
                    }

                    // Orijinal entry'yi ekle
                    groupedEntries[groupKey].originalEntries.push(entry);
                  });

                  // Grupları satırlara çevir
                  const rows = Object.values(groupedEntries).map((group, index) => ({
                    id: `edit-${Date.now()}-${index}`,
                    ...group
                  }));

                  setSingleRowData(rows);
                  setShowDataEntry(true);
                }
              } catch (error) {
                console.error('Error in onEdit handler:', error);
                alert('Düzenleme ekranı açılırken bir hata oluştu: ' + error.message);
              }
            }}
            onDelete={async (entries) => {
              if (!window.confirm('Seçili bütçeyi (tüm satırlarıyla) silmek istiyor musunuz?')) return;

              try {
                const deletePromises = (entries || [])
                  .filter(e => e.id && !isNaN(e.id) && Number.isInteger(Number(e.id)))
                  .map(e => apiRequest(`/marketing-budget/registries/${e.id}/`, { method: 'DELETE' }));

                await Promise.all(deletePromises);
                fetchExistingData(); // Veriyi yenile
                alert('Seçili bütçe silindi.');
              } catch (error) {
                // Silently handle error
                alert('Silme sırasında bir hata oluştu.');
              }
            }}
            onNew={() => {
              // Reset everything before showing modal
              setFormData({
                maingroup: '',
                budgettype: '',
                year: new Date().getFullYear(),
                month: null,
                entries: []
              });
              setSingleRowData([]);
              setShowDataEntry(true);
            }}
            onCreateFromPlanning={(entries) => {
              // Planlanan'dan gerçekleşen oluştur - klonla butonu

              if (entries && entries.length > 0) {
                const firstEntry = entries[0];

                // Form verilerini yükle (yeni ID'ler ile) - API'den gelen field isimlerini kullan
                setFormData({
                  maingroup: firstEntry.maingroup_id || firstEntry.maingroup,
                  budgettype: firstEntry.budgettype_id || firstEntry.budgettype,
                  year: new Date().getFullYear(), // Mevcut yıl
                  month: firstEntry.budget_month || null,
                  entries: [] // Boş entries, çünkü yeni kayıt olacak
                });

                const isRepresHos = firstEntry.maingroup_name === 'Temsil Ağırlama';
                const isAkkaBonus = firstEntry.maingroup_name?.includes('AKKA Bonus') || false;

                const groupedEntries = {};

                entries.forEach((entry) => {
                  // Gruplama anahtarı oluştur
                  let groupKey;
                  if (isAkkaBonus) {
                    groupKey = `${entry.hotel_name || entry.hotel || ''}`;
                  } else if (isRepresHos) {
                    groupKey = `${entry.market_id || entry.market || ''}-${entry.country || entry.country_name || ''}-${entry.city || entry.city_name || ''}-${entry.agency || entry.agency_name || ''}-${entry.budget_month || 'no-month'}`;
                  } else {
                    groupKey = `${entry.market_id || entry.market || ''}-${entry.country || entry.country_name || ''}-${entry.city || entry.city_name || ''}-${entry.agency || entry.agency_name || ''}`;
                  }

                  if (!groupedEntries[groupKey]) {
                    const entryToUse = { ...entry };
                    // ID'leri temizle
                    delete entryToUse.id;
                    delete entryToUse.uuid;

                    groupedEntries[groupKey] = {
                      ...entryToUse,
                      market: entry.market_id || entry.market || '',
                      subgroupAmounts: {},
                      subgroupPax: {},
                      originalEntries: []
                    };
                  }

                  // Alt grup tutarını ekle
                  const subgroupId = entry.subgroup_id || entry.subgroup;
                  const budgetAmount = entry.budget_amount || entry.amount || entry.tutar;

                  if (subgroupId && budgetAmount) {
                    groupedEntries[groupKey].subgroupAmounts[subgroupId] = budgetAmount;
                  }

                  // AKKA Bonus ve Temsil Ağırlama için PAX değerini ekle
                  if ((isAkkaBonus || isRepresHos) && subgroupId && entry.pax) {
                    groupedEntries[groupKey].subgroupPax[subgroupId] = entry.pax;
                  }
                });

                // Grupları satırlara çevir
                const rows = Object.values(groupedEntries).map((group, index) => ({
                  id: `new-${Date.now()}-${index}`,
                  ...group
                }));

                setSingleRowData(rows);
              }

              // Modal'ı aç
              setTimeout(() => {
                setShowDataEntry(true);
              }, 100);
            }}
            refreshTrigger={refreshTrigger}
          />
        </div>
      </div>
    );
  }

  // Tam Ekran Modal - Temel Bilgiler Üstte, Veri Girişi Altta
  // Modal render hatasını yakalamak için try-catch
  let modalContent;
  try {
    modalContent = (
      <>
        {/* Tam Ekran Modal */}
        <div className="fixed inset-0 bg-black/50 z-[9999] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 w-full h-full max-w-[98%] max-h-[98%] rounded-xl shadow-2xl flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Yeni Bütçe Ekle
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowDataEntry(false);
                  setFormData({
                    maingroup: '',
                    budgettype: '',
                    year: new Date().getFullYear(),
                    month: null,
                    entries: []
                  });
                  setSingleRowData([]);
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

          {/* Temel Bilgiler - Kompakt */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {/* Ana Grup */}
              <div>
                <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                  Ana Grup *
                </label>
                <select
                  value={formData.maingroup}
                  onChange={(e) => setFormData(prev => ({ ...prev, maingroup: e.target.value }))}
                  className="w-full px-2 py-1 border border-blue-300 dark:border-blue-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                >
                  <option value="">Seçin</option>
                  {referenceData.maingroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.maingroup_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Bütçe Tipi */}
              <div>
                <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                  Bütçe Tipi *
                </label>
                <select
                  value={formData.budgettype}
                  onChange={(e) => setFormData(prev => ({ ...prev, budgettype: e.target.value }))}
                  className="w-full px-2 py-1 border border-blue-300 dark:border-blue-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                >
                  <option value="">Seçin</option>
                  {referenceData.types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.budget_type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Yıl */}
              <div>
                <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                  Yıl *
                </label>
                <select
                  value={formData.year}
                  onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                  className="w-full px-2 py-1 border border-blue-300 dark:border-blue-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                >
                  {Array.from({ length: 10 }, (_, i) => 2020 + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>



              {/* Bütçe Adı - Yan yana */}
              {formData.maingroup && formData.budgettype && (
                <div className="col-span-2 md:col-span-2 lg:col-span-2">
                  <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                    Bütçe Adı
                  </label>
                  <div className="px-2 py-1 bg-blue-100 dark:bg-blue-800 rounded text-xs text-blue-800 dark:text-blue-200">
                    {(() => {
                      const selectedGroup = (referenceData.maingroups || []).find(g => parseInt(g.id) === parseInt(formData.maingroup));
                      const selectedType = (referenceData.types || []).find(t => parseInt(t.id) === parseInt(formData.budgettype));
                      return `${selectedGroup?.maingroup_name} - ${selectedType?.budget_type} (${formData.year})`;
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Veri Girişi - Ana Alan */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Excel Benzeri Tablo - Tam Genişlik */}
            <div className="flex-1 p-4 relative budget-table-container" style={{ height: '700px', overflow: 'scroll', scrollbarWidth: 'thin', scrollbarColor: '#888 #f1f1f1' }}>
              <style>{`
                .budget-table-container::-webkit-scrollbar {
                  width: 12px !important;
                  height: 12px !important;
                  display: block !important;
                }
                .budget-table-container::-webkit-scrollbar-track {
                  background: #f1f1f1 !important;
                  border-radius: 6px !important;
                  display: block !important;
                }
                .budget-table-container::-webkit-scrollbar-thumb {
                  background: #888 !important;
                  border-radius: 6px !important;
                  display: block !important;
                }
                .budget-table-container::-webkit-scrollbar-thumb:hover {
                  background: #555 !important;
                }
                .budget-table-container::-webkit-scrollbar-corner {
                  background: #f1f1f1 !important;
                }
                
                /* Sticky header için güçlü background */
                .budget-table-container thead tr {
                  background-color: #f9fafb !important;
                }
                .dark .budget-table-container thead tr {
                  background-color: #374151 !important;
                }
                .budget-table-container thead th {
                  background-color: #f9fafb !important;
                }
                .dark .budget-table-container thead th {
                  background-color: #374151 !important;
                }
              `}</style>
              {formData.maingroup ? (() => {
                try {
                  return renderStep3();
                } catch (err) {
                  console.error('renderStep3 error:', err);
                  return (
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                          <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                        </div>
                        <h4 className="text-lg font-medium text-red-700 dark:text-red-400 mb-2">
                          Veri yüklenirken hata oluştu
                        </h4>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                          {err.message}
                        </p>
                        <button
                          onClick={() => {
                            setShowDataEntry(false);
                            setFormData({ maingroup: '', budgettype: '', year: new Date().getFullYear(), month: null, entries: [] });
                            setSingleRowData([]);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Geri Dön
                        </button>
                      </div>
                    </div>
                  );
                }
              })() : (
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Veri Girişi İçin Ana Grup Seçin
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Üst kısımdan ana grup seçtikten sonra veri girişi yapabilirsiniz
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={addNewRow}
                className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Satır Ekle
              </button>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {singleRowData.length > 0 && (
                  <span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {singleRowData.length}
                    </span> satır hazır
                  </span>
                )}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDataEntry(false);
                  setFormData({
                    maingroup: '',
                    budgettype: '',
                    year: new Date().getFullYear(),
                    month: null,
                    entries: []
                  });
                  setSingleRowData([]);
                }}
                className="px-6 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                İptal
              </button>

              <button
                onClick={handleSave}
                disabled={loading || !formData.maingroup || !formData.budgettype || singleRowData.length === 0 || singleRowData.every(row => {
                  // Seçili ana grubu al
                  const selectedGroup = (referenceData.maingroups || []).find(g => parseInt(g.id) === parseInt(formData.maingroup));
                  const isRepresHos = selectedGroup?.maingroup_name === 'Temsil Ağırlama';
                  const isAkkaBonus = selectedGroup?.maingroup_name?.includes('AKKA Bonus') || false;
                  const isWebSoftware = selectedGroup?.maingroup_name === 'Web Yazılım';

                  // AKKA Bonus için özel kontrol: hem subgroupAmounts hem de subgroupPax kontrolü
                  if (isAkkaBonus) {
                    const hasAmounts = Object.values(row.subgroupAmounts || {}).some(amount => amount && parseFloat(amount) > 0);
                    const hasPax = Object.values(row.subgroupPax || {}).some(pax => pax && parseFloat(pax) > 0);
                    return !hasAmounts && !hasPax; // En az bir değer (TUTAR veya PAX) olmalı
                  }

                  // Temsil Ağırlama ve Web Yazılım için market kontrolü yok
                  if (isRepresHos || isWebSoftware) {
                    return Object.values(row.subgroupAmounts || {}).every(amount => !amount || parseFloat(amount) <= 0);
                  }

                  // Diğer ana gruplar için market zorunlu
                  return !row.market || Object.values(row.subgroupAmounts || {}).every(amount => !amount || parseFloat(amount) <= 0);
                })}
                className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Kaydet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Yeni Şehir Ekleme Modal */}
      {isAddingNewCity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Yeni Şehir Ekle
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Şehir Adı
                </label>
                <input
                  type="text"
                  value={newCityName}
                  onChange={(e) => setNewCityName(e.target.value)}
                  placeholder="Şehir adını girin..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsAddingNewCity(false);
                    setNewCityName('');
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleAddNewCity}
                  disabled={!newCityName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Pazar Ekleme Modal */}
      {isAddingNewMarket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Yeni Pazar Ekle
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pazar Adı
                </label>
                <input
                  type="text"
                  value={newMarketName}
                  onChange={(e) => setNewMarketName(e.target.value)}
                  placeholder="Pazar adını girin..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsAddingNewMarket(false);
                    setNewMarketName('');
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleAddNewMarket}
                  disabled={!newMarketName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Ülke Ekleme Modal */}
      {isAddingNewCountry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Yeni Ülke Ekle
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ülke Adı
                </label>
                <input
                  type="text"
                  value={newCountryName}
                  onChange={(e) => setNewCountryName(e.target.value)}
                  placeholder="Ülke adını girin..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsAddingNewCountry(false);
                    setNewCountryName('');
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleAddNewCountry}
                  disabled={!newCountryName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Acenta Ekleme Modal */}
      {isAddingNewAgency && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Yeni Acenta Ekle
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Acenta Adı
                </label>
                <input
                  type="text"
                  value={newAgencyName}
                  onChange={(e) => setNewAgencyName(e.target.value)}
                  placeholder="Acenta adını girin..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsAddingNewAgency(false);
                    setNewAgencyName('');
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleAddNewAgency}
                  disabled={!newAgencyName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Otel Ekleme Modal */}
      {isAddingNewHotel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Yeni Otel Ekle
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Otel Adı
                </label>
                <input
                  type="text"
                  value={newHotelName}
                  onChange={(e) => setNewHotelName(e.target.value)}
                  placeholder="Otel adını girin..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsAddingNewHotel(false);
                    setNewHotelName('');
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleAddNewHotel}
                  disabled={!newHotelName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
    );
  } catch (modalRenderError) {
    console.error('Modal render error:', modalRenderError);
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-lg p-8">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <ExclamationTriangleIcon className="h-10 w-10 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            Modal Render Hatası
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {modalRenderError?.message || 'Beklenmeyen bir hata oluştu.'}
          </p>
          <pre className="text-left text-xs text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg mb-4 max-h-40 overflow-auto whitespace-pre-wrap">
            {modalRenderError?.stack || String(modalRenderError)}
          </pre>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Sayfayı Yenile
          </button>
        </div>
      </div>
    );
  }

  return modalContent;
};

const BasicBudgetWithErrorBoundary = (props) => (
  <ErrorBoundary
    fallback={({ error, resetError }) => (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-lg p-8">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <ExclamationTriangleIcon className="h-10 w-10 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            Bütçe Ekranında Hata Oluştu
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {error?.message || 'Beklenmeyen bir hata oluştu.'}
          </p>
          {process.env.NODE_ENV === 'development' && (
            <pre className="text-left text-xs text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg mb-4 max-h-40 overflow-auto">
              {error?.stack}
            </pre>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={resetError}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Tekrar Dene
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
            >
              Sayfayı Yenile
            </button>
          </div>
        </div>
      </div>
    )}
  >
    <BasicBudget {...props} />
  </ErrorBoundary>
);

export default BasicBudgetWithErrorBoundary;
